import { useEffect, useState } from "react";
import BoardSelector from "./BoardSelector";
import BoardManagementModal from "./BoardManagementModal";
import { useRebalanceBoards, type RebalanceBoard } from "./boards";

interface RebalanceQueryControlsProps {
  query: string;
  onQueryChange: (query: string) => void;
  /** Called at each board selection in "Boards", "No board" too. */
  onBoardSelect?: (boardId: number | null) => void;
}

const RebalanceQueryControls = ({
  query,
  onQueryChange,
  onBoardSelect,
}: RebalanceQueryControlsProps) => {
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem("rebalances-selected-board-id");
    return saved ? parseInt(saved) : null;
  });
  const [showModal, setShowModal] = useState(false);

  // Every board, walked page by page from `GET /v1/rebalances/boards`, so the
  // selected board's query can be applied once the boards are loaded.
  const { boards, reload: reloadBoards } = useRebalanceBoards();

  // Bumped after each board write in the modal. `BoardSelector` is keyed on
  // it, so it remounts and walks the boards again: its walk has finished and
  // would not ask the server again on its own.
  const [boardsRevision, setBoardsRevision] = useState(0);

  const refreshBoards = () => {
    reloadBoards();
    setBoardsRevision((revision) => revision + 1);
  };

  // When a board is selected (or boards finish loading), apply its query.
  // APP-1279: this no longer clears the query when no board is selected —
  // clearing happens only on explicit deselect (see handleBoardSelect) so an
  // async board load can't wipe a URL-seeded query from an insights deep link.
  useEffect(() => {
    if (selectedBoardId !== null && boards.length > 0) {
      const selectedBoard = boards.find((b) => b.id === selectedBoardId);
      if (selectedBoard && selectedBoard.query !== query) {
        onQueryChange(selectedBoard.query);
      }
    }
  }, [selectedBoardId, boards]); // Removed onQueryChange and query from deps to avoid loops

  // Handle board selection
  const handleBoardSelect = (boardId: number | null) => {
    setSelectedBoardId(boardId);
    onBoardSelect?.(boardId);
    if (boardId === null) {
      // Explicit "No board" → clear the active query.
      onQueryChange("");
    }
    // For a selected board, the useEffect above applies its query.
  };

  const handleBoardCreated = (board: RebalanceBoard) => {
    refreshBoards();
    // Automatically select the newly created board
    setSelectedBoardId(board.id);
    onQueryChange(board.query);
  };

  const handleBoardUpdated = (board: RebalanceBoard) => {
    refreshBoards();
    // If the updated board is currently selected, update the query
    if (selectedBoardId === board.id) {
      onQueryChange(board.query);
    }
  };

  const handleBoardDeleted = (boardId: number) => {
    refreshBoards();
    // If the deleted board was selected, clear the selection and its query.
    if (selectedBoardId === boardId) {
      setSelectedBoardId(null);
      onQueryChange("");
    }
  };

  return (
    <div className="column-search-group">
      <BoardSelector
        key={boardsRevision}
        selectedBoardId={selectedBoardId}
        onSelect={handleBoardSelect}
        onManageClick={() => setShowModal(true)}
      />

      <BoardManagementModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onBoardCreated={handleBoardCreated}
        onBoardUpdated={handleBoardUpdated}
        onBoardDeleted={handleBoardDeleted}
      />
    </div>
  );
};

export default RebalanceQueryControls;
